import { useMemo, useState } from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { useGetAllCompaniesQuery } from "app/api/companies";
import SimpleDialog from "components/SimpleDialog";
import { Order } from "models/Order";
import Spinner from "features/spinner/Spinner";
import { Company } from "models/Company";

type SelectCompanyDialogProps = {
  title: string;
  order: Order;
  initialCompany?: Company;
  allowNull?: boolean;
  onClose: () => void;
  onSelect: (company: Company) => void;
};

const SelectCompanyDialog: React.FC<SelectCompanyDialogProps> = (props) => {
  const { title, order, allowNull, initialCompany, onClose, onSelect } = props;
  const { data: allCompanies, isLoading: isLoadingCompanies } = useGetAllCompaniesQuery();
  const availableCompanies = useMemo(() => {
    if (!allCompanies) return [];

    return allCompanies
      ?.filter(
        // Only select companies that...
        (company) =>
          // have a UE code
          !!company.ueCode &&
          // are not already assigned as primary
          order.assignedToCompany !== company.ueCode &&
          // are not already assigned as additional
          !order.additionalCompanies.some((assigned) => assigned.uecode === company.ueCode)
      )
      .sort((a, b) => (a.ueCode ?? "").localeCompare(b.ueCode ?? ""));
  }, [allCompanies, order]);

  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    initialCompany?.id || null
  );

  return (
    <SimpleDialog
      title={title}
      onClose={onClose}
      onConfirm={() => {
        const company = allCompanies?.find((c) => c.id === selectedCompanyId);
        onSelect(company!);
        onClose();
      }}
      confirmProps={{
        disabled: !allowNull && !selectedCompanyId,
      }}
    >
      {isLoadingCompanies ? (
        <Box sx={{ display: "grid", placeItems: "center", gap: 2 }}>
          <Spinner size="3rem" color="grey" />
          <Typography>Laddar företag...</Typography>
        </Box>
      ) : (
        <TextField
          select
          label="Välj företag"
          variant="filled"
          fullWidth
          value={selectedCompanyId || ""}
          onChange={(e) => setSelectedCompanyId(e.target.value)}
        >
          {availableCompanies.map((company) => (
            <MenuItem key={company.id} value={company.id}>
              {company.ueCode}
            </MenuItem>
          ))}
        </TextField>
      )}
    </SimpleDialog>
  );
};

export default SelectCompanyDialog;
